import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, RadioGroup, FormControlLabel, Radio, TextField, Popover, Paper, Typography } from '@mui/material';

const DateRangePicker = ({
  dateFilter,
  onDateFilterChange,
  dateRanges,
  customStartDate,
  customEndDate,
  onCustomStartDateChange,
  onCustomEndDateChange,
}) => {
  const [datePickerAnchorEl, setDatePickerAnchorEl] = useState(null);
  const [tempStartDate, setTempStartDate] = useState(customStartDate);
  const [tempEndDate, setTempEndDate] = useState(customEndDate);

  const handleDateFilterChange = (e) => {
    const value = e.target.value;
    onDateFilterChange(value);
    if (value === 'custom') {
      setDatePickerAnchorEl(e.target.closest('.MuiRadioGroup-root'));
    } else {
      setDatePickerAnchorEl(null);
    }
  };

  const handleDatePickerClose = () => {
    if (dateFilter === 'custom' && !customStartDate && !customEndDate) {
      onDateFilterChange('currentMonth');
    }
    setDatePickerAnchorEl(null);
  };

  const handleDatePickerApply = () => {
    onCustomStartDateChange(tempStartDate);
    onCustomEndDateChange(tempEndDate);
    setDatePickerAnchorEl(null);
  };

  return (
    <>
      <RadioGroup
        row
        value={dateFilter}
        onChange={handleDateFilterChange}
        sx={{
          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
          backgroundColor: 'white',
          pl: 0,
          py: 1,
          borderRadius: 1,
          width: { xs: '100%', md: 'auto' },
          justifyContent: { xs: 'center', md: 'flex-end' },
          '& .MuiFormControlLabel-root': {
            m: 0,
            mx: 1,
            position: 'relative',
          },
          '& .MuiRadio-root': {
            display: 'none',
          },
          '& .MuiFormControlLabel-label': {
            py: 1,
            px: 2,
            borderRadius: 1,
            fontSize: '0.875rem',
            transition: 'all 0.2s',
            zIndex: 1,
          },
          '& .Mui-checked + .MuiFormControlLabel-label': {
            color: 'primary.contrastText',
            backgroundColor: 'primary.main',
          },
        }}
      >
        <FormControlLabel value="previousMonth" control={<Radio />} label={dateRanges.previousMonth.label} />
        <FormControlLabel value="currentMonth" control={<Radio />} label={dateRanges.currentMonth.label} />
        <FormControlLabel value="custom" control={<Radio />} label="Custom" />
      </RadioGroup>

      {/* Date Picker Popover */}
      <Popover
        open={Boolean(datePickerAnchorEl)}
        anchorEl={datePickerAnchorEl}
        onClose={handleDatePickerClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 2,
            width: 300,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            overflow: 'visible',
            '&::before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: -5,
              right: 20,
              width: 12,
              height: 12,
              bgcolor: 'background.paper',
              transform: 'rotate(45deg)',
              zIndex: 0,
              boxShadow: '-2px -2px 3px rgba(0,0,0,0.05)',
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            position: 'relative',
            bgcolor: 'background.paper',
            zIndex: 1,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
            Select Date Range
          </Typography>
          <TextField
            label="Start Date"
            type="date"
            value={tempStartDate}
            onChange={(e) => setTempStartDate(e.target.value)}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            type="date"
            value={tempEndDate}
            onChange={(e) => setTempEndDate(e.target.value)}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
            <Typography
              onClick={handleDatePickerClose}
              sx={{
                cursor: 'pointer',
                color: 'text.secondary',
                '&:hover': { color: 'text.primary' },
                py: 1,
                px: 2,
              }}
            >
              Cancel
            </Typography>
            <Paper
              onClick={handleDatePickerApply}
              sx={{
                cursor: 'pointer',
                bgcolor: 'primary.main',
                color: 'white',
                py: 1,
                px: 2,
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            >
              Apply
            </Paper>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

DateRangePicker.propTypes = {
  dateFilter: PropTypes.string.isRequired,
  onDateFilterChange: PropTypes.func.isRequired,
  dateRanges: PropTypes.shape({
    previousMonth: PropTypes.shape({
      label: PropTypes.string.isRequired,
    }).isRequired,
    currentMonth: PropTypes.shape({
      label: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  customStartDate: PropTypes.string,
  customEndDate: PropTypes.string,
  onCustomStartDateChange: PropTypes.func.isRequired,
  onCustomEndDateChange: PropTypes.func.isRequired,
};

export default DateRangePicker;
